/** Idempotency keys.
 *
 *  `crypto.randomUUID()` is only defined in a **secure context**. `localhost`
 *  is one; `http://192.168.x.x:5173` — a phone hitting the dev server over the
 *  LAN — is not, so it is `undefined` there and throws. That is the ordinary way
 *  this app gets tested on a device, so it cannot depend on it.
 *
 *  `crypto.getRandomValues` carries no such restriction, so build the UUID from
 *  it. Strength matters here beyond mere uniqueness: a colliding key replayed
 *  with a different body is a 409, and in U7's offline queue that would surface
 *  as a mutation that silently never lands.
 */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();

  if (typeof c?.getRandomValues === 'function') {
    const bytes = c.getRandomValues(new Uint8Array(16));
    // RFC 9562 §5.4: version 4, variant 10xx.
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20, 32),
    ].join('-');
  }

  throw new Error(
    'No CSPRNG available, so an idempotency key cannot be minted safely. ' +
      'Serve the app over https or from localhost.',
  );
}
